import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Nav, Row, Table } from 'react-bootstrap';
import { isChangeManager } from '../changePermissions';
import { useAuth } from '../../../context/AuthContext';
import { useModal } from '../../../context/ModalContext';
import { changesApi } from '../../../services/changesApi';
import type {
  ChangeAgendaEmailSchedule,
  ChangeAgendaEmailTemplate,
  ChangeCommittee,
  ChangeCommitteePerson,
} from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import AgendaEmailVariablesHelp from './AgendaEmailVariablesHelp';

const PEOPLE_PAGE = 20;

type PanelSection = 'people' | 'email';

interface Props {
  initialSection?: PanelSection;
  layoutOnly?: boolean;
}

const ChangeCommitteesPanel: React.FC<Props> = ({
  initialSection = 'people',
  layoutOnly = false,
}) => {
  const { user } = useAuth();
  const { showAlert, showConfirm } = useModal();
  const canManage = isChangeManager(user);
  const [items, setItems] = useState<ChangeCommittee[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [people, setPeople] = useState<ChangeCommitteePerson[]>([]);
  const [peopleTotal, setPeopleTotal] = useState(0);
  const [peopleSkip, setPeopleSkip] = useState(0);
  const [newCommitteeName, setNewCommitteeName] = useState('');
  const [renameDrafts, setRenameDrafts] = useState<Record<number, string>>({});
  const [personName, setPersonName] = useState('');
  const [personEmail, setPersonEmail] = useState('');
  const [layouts, setLayouts] = useState<ChangeAgendaEmailTemplate[]>([]);
  const [schedules, setSchedules] = useState<ChangeAgendaEmailSchedule[]>([]);
  const [section, setSection] = useState<PanelSection>(layoutOnly ? 'email' : initialSection);
  const [layoutKey, setLayoutKey] = useState<string>('global');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const selected = items.find((item) => item.id === selectedId) || null;

  const loadCommittees = useCallback(async () => {
    setLoading(true);
    try {
      const response = await changesApi.listCommittees({ skip: 0, limit: 50 });
      setItems(response.items || []);
      setError(null);
      setSelectedId((current) => {
        if (current && response.items.some((item) => item.id === current)) return current;
        return response.items[0]?.id ?? null;
      });
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao carregar os comitês.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPeople = useCallback(async () => {
    if (!selectedId) {
      setPeople([]);
      setPeopleTotal(0);
      return;
    }
    try {
      const response = await changesApi.listCommitteePeople(selectedId, {
        skip: peopleSkip,
        limit: PEOPLE_PAGE,
      });
      setPeople(response.items || []);
      setPeopleTotal(response.total || 0);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao carregar as pessoas do comitê.'));
    }
  }, [selectedId, peopleSkip]);

  const loadEmailConfig = useCallback(async () => {
    try {
      const [layoutResponse, scheduleResponse] = await Promise.all([
        changesApi.listAgendaEmailLayouts(),
        changesApi.listAgendaEmailSchedules(),
      ]);
      setLayouts(layoutResponse.items || []);
      setSchedules(scheduleResponse.items || []);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao carregar layouts e horários de e-mail.'));
    }
  }, []);

  useEffect(() => {
    void loadCommittees();
  }, [loadCommittees]);

  useEffect(() => {
    void loadPeople();
  }, [loadPeople]);

  useEffect(() => {
    void loadEmailConfig();
  }, [loadEmailConfig]);

  const handleCreate = async () => {
    const name = newCommitteeName.trim();
    if (name.length < 2) {
      showAlert({
        title: 'Nome do comitê',
        message: 'Informe um nome com ao menos 2 caracteres.',
        variant: 'warning',
      });
      return;
    }
    try {
      const created = await changesApi.createCommittee({ name });
      setNewCommitteeName('');
      await loadCommittees();
      setSelectedId(created.id);
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível criar o comitê.'),
        variant: 'danger',
      });
    }
  };

  const handleRename = async (item: ChangeCommittee) => {
    const name = (renameDrafts[item.id] ?? item.name).trim();
    if (name.length < 2) return;
    try {
      await changesApi.updateCommittee(item.id, { name });
      await loadCommittees();
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível renomear o comitê.'),
        variant: 'danger',
      });
    }
  };

  const handleToggleActive = async (item: ChangeCommittee) => {
    const confirmed = await showConfirm({
      title: item.is_active ? 'Desativar comitê' : 'Ativar comitê',
      message: item.is_active
        ? `Desativar ${item.name}? Ele deixa de receber a pauta.`
        : `Ativar ${item.name}?`,
      variant: item.is_active ? 'warning' : 'primary',
      confirmLabel: item.is_active ? 'Desativar' : 'Ativar',
      cancelLabel: 'Cancelar',
    });
    if (!confirmed) return;
    try {
      await changesApi.updateCommittee(item.id, { is_active: !item.is_active });
      await loadCommittees();
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível atualizar o comitê.'),
        variant: 'danger',
      });
    }
  };

  const handleDelete = async (item: ChangeCommittee) => {
    if (item.is_system) return;
    const confirmed = await showConfirm({
      title: 'Excluir comitê',
      message: `Excluir ${item.name} e as pessoas cadastradas?`,
      variant: 'danger',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!confirmed) return;
    try {
      await changesApi.deleteCommittee(item.id);
      await loadCommittees();
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível excluir o comitê.'),
        variant: 'danger',
      });
    }
  };

  const handleAddPerson = async () => {
    if (!selectedId) return;
    if (personName.trim().length < 2 || !personEmail.trim()) {
      showAlert({
        title: 'Nome e e-mail',
        message: 'Informe nome e e-mail para incluir a pessoa no comitê.',
        variant: 'warning',
      });
      return;
    }
    try {
      await changesApi.addCommitteePerson(selectedId, {
        name: personName.trim(),
        email: personEmail.trim(),
      });
      setPersonName('');
      setPersonEmail('');
      await Promise.all([loadPeople(), loadCommittees()]);
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível incluir a pessoa.'),
        variant: 'danger',
      });
    }
  };

  const handleToggleReceives = async (person: ChangeCommitteePerson) => {
    if (!selectedId) return;
    try {
      await changesApi.updateCommitteePerson(selectedId, person.id, {
        receives_email: !person.receives_email,
      });
      await loadPeople();
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível atualizar o recebimento.'),
        variant: 'danger',
      });
    }
  };

  const handleDeletePerson = async (person: ChangeCommitteePerson) => {
    if (!selectedId) return;
    const confirmed = await showConfirm({
      title: 'Remover pessoa',
      message: `Remover ${person.name} deste comitê?`,
      variant: 'danger',
      confirmLabel: 'Remover',
      cancelLabel: 'Cancelar',
    });
    if (!confirmed) return;
    try {
      await changesApi.deleteCommitteePerson(selectedId, person.id);
      await Promise.all([loadPeople(), loadCommittees()]);
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível remover a pessoa.'),
        variant: 'danger',
      });
    }
  };

  const patchActiveLayout = (patch: Partial<ChangeAgendaEmailTemplate>) => {
    setLayouts((prev) =>
      prev.map((item) => ((item.key || item.scope) === layoutKey ? { ...item, ...patch } : item)),
    );
  };

  const handleSaveLayout = async () => {
    const current = layouts.find((item) => (item.key || item.scope) === layoutKey);
    if (!current) return;
    try {
      const payload = {
        enabled: current.enabled,
        subject: current.subject,
        body_html: current.body_html,
      };
      const saved =
        current.scope === 'global' || current.key === 'global'
          ? await changesApi.saveGlobalAgendaEmailTemplate(payload)
          : await changesApi.saveCommitteeEmailTemplate(Number(current.committee_id), payload);
      setLayouts((prev) =>
        prev.map((item) =>
          (item.key || item.scope) === layoutKey
            ? { ...item, ...saved, key: item.key, label: item.label, default_for: item.default_for }
            : item,
        ),
      );
      showAlert({ title: 'Layout', message: 'Texto do e-mail salvo.', variant: 'success' });
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível salvar o layout.'),
        variant: 'danger',
      });
    }
  };

  const handleSaveSchedules = async () => {
    try {
      const saved = await changesApi.saveAgendaEmailSchedules({
        items: schedules.map((item) => ({
          agenda_kind: item.agenda_kind,
          enabled: item.enabled,
          offset_days: item.offset_days,
          send_time: item.send_time,
        })),
      });
      setSchedules(saved.items || []);
      showAlert({ title: 'Horário', message: 'Envio automático salvo.', variant: 'success' });
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível salvar o horário automático.'),
        variant: 'danger',
      });
    }
  };

  const activeLayout = layouts.find((item) => (item.key || item.scope) === layoutKey) || layouts[0] || null;

  return (
    <div>
      <p className="text-muted small mb-3">
        {layoutOnly
          ? 'Layout pronto do e-mail da reunião. No calendário e no CAB, «Escrever e-mail» puxa este texto e preenche as N mudanças da pauta.'
          : 'Quem recebe o e-mail da pauta. Estas pessoas não ganham conta no SupportNect. Área de mudança continua só para quem aprova.'}
      </p>
      {error ? (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {layoutOnly ? null : (
        <Nav
          variant="tabs"
          role="tablist"
          className="mb-3"
          activeKey={section}
          onSelect={(key) => setSection((key as PanelSection) || 'people')}
        >
          <Nav.Item>
            <Nav.Link eventKey="people" role="tab" aria-selected={section === 'people'}>
              Pessoas
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="email" role="tab" aria-selected={section === 'email'}>
              E-mail da pauta
            </Nav.Link>
          </Nav.Item>
        </Nav>
      )}

      {section === 'people' ? (
        <>
          {canManage ? (
            <Row className="g-2 mb-3">
              <Col md={5}>
                <Form.Control
                  value={newCommitteeName}
                  onChange={(e) => setNewCommitteeName(e.target.value)}
                  placeholder="Novo comitê (ex.: Comitê de Infraestrutura)"
                  aria-label="Nome do novo comitê"
                />
              </Col>
              <Col md="auto">
                <Button onClick={() => void handleCreate()}>Criar comitê</Button>
              </Col>
            </Row>
          ) : null}
          {loading ? <div className="text-muted small">Carregando comitês…</div> : null}
          <Row className="g-3">
            <Col md={5}>
              <Table size="sm" hover>
                <thead>
                  <tr>
                    <th>Comitê</th>
                    <th>Pessoas</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      className={item.id === selectedId ? 'table-active' : undefined}
                      onClick={() => {
                        setSelectedId(item.id);
                        setPeopleSkip(0);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className="fw-semibold">{item.name}</div>
                        {!item.is_active ? (
                          <div className="small text-muted">Inativo — não recebe pauta</div>
                        ) : item.is_system ? (
                          <div className="small text-muted">Padrão do sistema</div>
                        ) : null}
                      </td>
                      <td>{item.people_count ?? 0}</td>
                      <td>
                        {canManage ? (
                          <div className="d-flex flex-wrap gap-1">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleToggleActive(item);
                              }}
                            >
                              {item.is_active ? 'Desativar' : 'Ativar'}
                            </Button>
                            {!item.is_system ? (
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleDelete(item);
                                }}
                              >
                                Excluir
                              </Button>
                            ) : null}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
            <Col md={7}>
              {selected ? (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">{selected.name}</h6>
                    {!selected.is_active ? <Badge bg="secondary">Inativo</Badge> : null}
                  </div>
                  {canManage ? (
                    <Row className="g-2 mb-3">
                      <Col>
                        <Form.Control
                          value={renameDrafts[selected.id] ?? selected.name}
                          onChange={(e) =>
                            setRenameDrafts((prev) => ({ ...prev, [selected.id]: e.target.value }))
                          }
                          aria-label="Renomear comitê"
                        />
                      </Col>
                      <Col md="auto">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => void handleRename(selected)}
                        >
                          Renomear
                        </Button>
                      </Col>
                    </Row>
                  ) : null}
                  {canManage ? (
                    <Row className="g-2 mb-3">
                      <Col md={5}>
                        <Form.Control
                          value={personName}
                          onChange={(e) => setPersonName(e.target.value)}
                          placeholder="Nome"
                          aria-label="Nome da pessoa do comitê"
                        />
                      </Col>
                      <Col md={5}>
                        <Form.Control
                          type="email"
                          value={personEmail}
                          onChange={(e) => setPersonEmail(e.target.value)}
                          placeholder="E-mail"
                          aria-label="E-mail da pessoa do comitê"
                        />
                      </Col>
                      <Col md="auto">
                        <Button size="sm" onClick={() => void handleAddPerson()}>
                          Incluir pessoa
                        </Button>
                      </Col>
                    </Row>
                  ) : null}
                  {people.length === 0 ? (
                    <div className="text-muted small mb-3">Nenhuma pessoa neste comitê.</div>
                  ) : (
                    <Table size="sm">
                      <thead>
                        <tr>
                          <th>Nome</th>
                          <th>E-mail</th>
                          <th>Recebe</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {people.map((person) => (
                          <tr key={person.id}>
                            <td>{person.name}</td>
                            <td>{person.email}</td>
                            <td>
                              <Badge bg={person.receives_email ? 'success' : 'secondary'}>
                                {person.receives_email ? 'Sim' : 'Não'}
                              </Badge>
                            </td>
                            <td>
                              {canManage ? (
                                <div className="d-flex flex-wrap gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => void handleToggleReceives(person)}
                                  >
                                    {person.receives_email ? 'Desativar e-mail' : 'Ativar e-mail'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => void handleDeletePerson(person)}
                                  >
                                    Remover
                                  </Button>
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                  {peopleTotal > PEOPLE_PAGE ? (
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={peopleSkip === 0}
                        onClick={() => setPeopleSkip((prev) => Math.max(0, prev - PEOPLE_PAGE))}
                      >
                        Anterior
                      </Button>
                      <span className="small text-muted">
                        {peopleSkip + 1}–{Math.min(peopleSkip + PEOPLE_PAGE, peopleTotal)} de {peopleTotal}
                      </span>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={peopleSkip + PEOPLE_PAGE >= peopleTotal}
                        onClick={() => setPeopleSkip((prev) => prev + PEOPLE_PAGE)}
                      >
                        Próxima
                      </Button>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="text-muted small">Selecione um comitê à esquerda.</div>
              )}
            </Col>
          </Row>
        </>
      ) : (
        <>
          <p className="small text-muted mb-3">
            Cada comitê tem o próprio texto. Ative ou desative o disparo, edite o título e a
            descrição e use {' {{pauta_html}} '} como anexo da lista de mudanças desta reunião. No
            calendário e no CAB, «Escrever e-mail» puxa este layout já preenchido com as N mudanças
            da pauta. Desligar um layout impede o envio automático daquela pauta.
          </p>
          <Form className="mb-3">
            {layouts.map((item) => {
              const key = item.key || item.scope;
              return (
                <Form.Check
                  key={key}
                  type="radio"
                  id={`layout-${key}`}
                  name="email-layout"
                  label={`${item.label || key}${item.enabled ? '' : ' (desligado)'}`}
                  checked={layoutKey === key}
                  onChange={() => setLayoutKey(key)}
                />
              );
            })}
          </Form>
          {activeLayout ? (
            <>
              {activeLayout.enabled ? null : (
                <Alert variant="warning" className="py-2">
                  Este layout está desligado. O envio automático dessa pauta não dispara.
                </Alert>
              )}
              <AgendaEmailVariablesHelp
                variables={activeLayout.variables}
                onInsertSubject={(token) =>
                  patchActiveLayout({ subject: `${activeLayout.subject} ${token}`.trim() })
                }
                onInsertBody={(token) =>
                  patchActiveLayout({
                    body_html: activeLayout.body_html
                      ? `${activeLayout.body_html}\n${token}`
                      : token,
                  })
                }
              />
              <Form.Check
                className="mb-2"
                type="switch"
                id="active-layout-enabled"
                label={layoutKey === 'global' ? 'Ativar layout global' : 'Ativar este layout'}
                checked={activeLayout.enabled}
                disabled={!canManage}
                onChange={(e) => patchActiveLayout({ enabled: e.target.checked })}
              />
              <Form.Label className="small">Título</Form.Label>
              <Form.Control
                className="mb-2"
                value={activeLayout.subject}
                disabled={!canManage}
                onChange={(e) => patchActiveLayout({ subject: e.target.value })}
                aria-label="Título do layout"
              />
              <Form.Label className="small">Descrição</Form.Label>
              <div className="d-flex flex-wrap gap-2 mb-2">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={!canManage}
                  onClick={() =>
                    patchActiveLayout({
                      body_html: activeLayout.body_html?.includes('{{reuniao.titulo}}')
                        ? activeLayout.body_html
                        : `<h2>{{reuniao.titulo}}</h2>\n${activeLayout.body_html || ''}`,
                    })
                  }
                >
                  Inserir título da reunião
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={!canManage}
                  onClick={() =>
                    patchActiveLayout({
                      body_html: activeLayout.body_html?.includes('{{pauta_html}}')
                        ? activeLayout.body_html
                        : `${activeLayout.body_html || ''}\n{{pauta_html}}`,
                    })
                  }
                >
                  Inserir anexo da pauta
                </Button>
              </div>
              <Form.Control
                as="textarea"
                rows={6}
                className="mb-3"
                value={activeLayout.body_html}
                disabled={!canManage}
                onChange={(e) => patchActiveLayout({ body_html: e.target.value })}
                aria-label="Descrição do layout"
              />
              <Form.Label className="small">Anexo da pauta</Form.Label>
              <p className="small text-muted mb-3">
                Campo de anexo: a lista das N mudanças desta reunião entra no e-mail com{' '}
                {'{{pauta_html}}'}. Cada reunião monta um anexo diferente.
              </p>
              {canManage ? (
                <Button size="sm" className="mb-4" onClick={() => void handleSaveLayout()}>
                  Salvar este layout
                </Button>
              ) : null}
            </>
          ) : (
            <div className="text-muted small mb-3">Carregando os layouts…</div>
          )}

          <h6 className="mt-2">Quando o e-mail sai sozinho</h6>
          <p className="small text-muted mb-3">
            Riscos começa na terça. CAMUD começa na quinta. Extraordinário só no envio manual.
          </p>
          {schedules.map((item, index) => (
            <div key={item.agenda_kind} className="border rounded p-2 mb-2">
              <div className="fw-semibold small mb-2">
                {item.agenda_kind === 'committee' ? 'Riscos e Impactos' : 'CAMUD Go/No-Go'}
                {item.pauta_weekday_label ? ` · pauta na ${item.pauta_weekday_label}` : ''}
              </div>
              <Form.Check
                className="mb-2"
                type="switch"
                id={`schedule-enabled-${item.agenda_kind}`}
                label="Ativar envio automático"
                checked={item.enabled}
                disabled={!canManage}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setSchedules((prev) =>
                    prev.map((row, rowIndex) => (rowIndex === index ? { ...row, enabled } : row)),
                  );
                }}
              />
              <Row className="g-2">
                <Col md={6}>
                  <Form.Select
                    size="sm"
                    value={
                      item.offset_days === 1 ? '1' : item.offset_days === 3 ? '3' : 'custom'
                    }
                    disabled={!canManage}
                    aria-label={`Quando enviar ${item.agenda_kind}`}
                    onChange={(e) => {
                      const value = e.target.value;
                      const offset_days = value === '3' ? 3 : value === '1' ? 1 : item.offset_days || 2;
                      setSchedules((prev) =>
                        prev.map((row, rowIndex) =>
                          rowIndex === index ? { ...row, offset_days } : row,
                        ),
                      );
                    }}
                  >
                    <option value="1">1 dia antes, no horário abaixo</option>
                    <option value="3">3 dias antes, no horário abaixo</option>
                    <option value="custom">Outra antecedência</option>
                  </Form.Select>
                </Col>
                {item.offset_days !== 1 && item.offset_days !== 3 ? (
                  <Col md={3}>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={1}
                      max={14}
                      value={item.offset_days}
                      disabled={!canManage}
                      aria-label={`Dias de antecedência ${item.agenda_kind}`}
                      onChange={(e) => {
                        const offset_days = Number(e.target.value) || 1;
                        setSchedules((prev) =>
                          prev.map((row, rowIndex) =>
                            rowIndex === index ? { ...row, offset_days } : row,
                          ),
                        );
                      }}
                    />
                  </Col>
                ) : null}
                <Col md={3}>
                  <Form.Control
                    size="sm"
                    type="time"
                    value={item.send_time}
                    disabled={!canManage}
                    aria-label={`Horário ${item.agenda_kind}`}
                    onChange={(e) => {
                      const send_time = e.target.value;
                      setSchedules((prev) =>
                        prev.map((row, rowIndex) =>
                          rowIndex === index ? { ...row, send_time } : row,
                        ),
                      );
                    }}
                  />
                </Col>
              </Row>
            </div>
          ))}
          {canManage && schedules.length > 0 ? (
            <Button size="sm" className="mt-2" variant="outline-primary" onClick={() => void handleSaveSchedules()}>
              Salvar horários automáticos
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
};

export default ChangeCommitteesPanel;
